import { memo } from 'react';
import { Popover } from 'antd';
import HeaderSortDescChevron from 'assets/svg/HeaderSortDescChevron';
import { TrendArrowDown } from 'assets/svg/TrendArrowDown';
import TrendArrowUp from 'assets/svg/TrendArrowUp';
import WhitespaceItemTooltip, { WhitespaceTooltipItem } from './WhitespaceItemTooltip';

const THIN_SCROLLBAR_CLASS =
    '[scrollbar-width:thin] [scrollbar-color:#a1a1aa_transparent] [&::-webkit-scrollbar]:w-1 [&::-webkit-scrollbar-thumb]:rounded-full [&::-webkit-scrollbar-thumb]:bg-[#a1a1aa] [&::-webkit-scrollbar-track]:bg-transparent';
const ITEM_NAME_CHAR_LIMIT = 20;

const truncateLabel = (label?: string) => {
    const value = label ?? '-';
    return value.length > ITEM_NAME_CHAR_LIMIT ? `${value.slice(0, ITEM_NAME_CHAR_LIMIT)}...` : value;
};

export type StaticWhitespaceCardProps = {
    valueSales: string;
    valuePrefix: string;
    valueSuffix: string;
    trendValue: number | null;
    trendDirection: 'up' | 'down' | 'neutral';
    avgValueSales: string;
    avgPrefix: string;
    shareLabel: string;
    crossSectionShareLabel: string;
    crossSectionLabel?: string;
    sharePercentage: number;
    items: WhitespaceTooltipItem[];
    moreCount: number;
    heatmapColor: string;
    onMoreClick?: () => void;
    isMoreLoading?: boolean;
    onCollapseClick?: () => void;
    showCollapseButton?: boolean;
    isWhitespace?: boolean;
    selectedYAxisKpiId?: string;
};

const StaticWhitespaceCard = memo(
    ({
        valueSales,
        valuePrefix,
        valueSuffix,
        trendValue,
        trendDirection,
        avgValueSales,
        avgPrefix,
        shareLabel,
        crossSectionShareLabel,
        crossSectionLabel = '',
        sharePercentage,
        items,
        moreCount,
        heatmapColor,
        onMoreClick,
        isMoreLoading = false,
        onCollapseClick,
        showCollapseButton = false,
        isWhitespace = false,
        selectedYAxisKpiId,
    }: StaticWhitespaceCardProps) => {
        return (
            <div
                className={`flex h-full w-[225px] flex-col items-start gap-2 self-stretch ${
                    isWhitespace ? 'border-[4px] border-[#71717b] p-[12px]' : 'p-[16px] border-none'
                }`}
                style={{ background: heatmapColor }}
            >
                <div className="flex w-full items-start justify-between">
                    <div className="leading-6 flex-1">
                        <div className="flex items-center gap-2">
                            <span className="text-[24px] font-bold text-blackTitle">{`${valuePrefix}${valueSales}${valueSuffix}`}</span>
                            {typeof trendValue === 'number' && trendValue !== 0 ? (
                                <span className={`text-[16px] leading-[20px] ${trendDirection === 'down' ? 'text-[#d92d20]' : 'text-[#007a41]'}`}>
                                    <span className="inline-flex items-center gap-0.5">
                                        {trendDirection === 'down' ? (
                                            <TrendArrowDown className="shrink-0" color="#d92d20" />
                                        ) : (
                                            <TrendArrowUp className="shrink-0" color="#007a41" />
                                        )}
                                        <span>{`${Math.abs(trendValue)}%`}</span>
                                    </span>
                                </span>
                            ) : null}
                        </div>
                        <div className="text-[16px] text-[#71717b]">{`Avg. ${avgPrefix}${avgValueSales}`}</div>
                    </div>
                    {isWhitespace ? <div>
                        <svg xmlns="http://www.w3.org/2000/svg" width="19" height="19" viewBox="0 0 19 19" fill="none">
                            <path d="M7.41667 13.7988V15.75C7.41667 16.6705 8.16286 17.4167 9.08333 17.4167C10.0038 17.4167 10.75 16.6705 10.75 15.75V13.7988M9.08333 0.75V1.58333M1.58333 9.08333H0.75M3.66667 3.66667L3.16659 3.16659M14.5 3.66667L15.0002 3.16659M17.4167 9.08333H16.5833M14.0833 9.08333C14.0833 11.8448 11.8448 14.0833 9.08333 14.0833C6.32191 14.0833 4.08333 11.8448 4.08333 9.08333C4.08333 6.32191 6.32191 4.08333 9.08333 4.08333C11.8448 4.08333 14.0833 6.32191 14.0833 9.08333Z" stroke="#18181B" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
                        </svg>
                    </div> : null}
                </div>

                <div className="flex w-full flex-col gap-1">
                    <div className="flex w-full items-end justify-between font-medium text-[#3f3f46]">
                        <span className="text-[12px] leading-normal">
                            <span>{shareLabel}</span>
                            {' '}
                            <span className="text-[#71717b]">Share</span>
                        </span>
                        {crossSectionShareLabel ? (
                            <span className="text-[10px] leading-normal">
                                <span>{crossSectionShareLabel}</span>
                                {crossSectionLabel ? (
                                    <>
                                        {' '}
                                        <span className="text-[#71717b]">{crossSectionLabel}</span>
                                    </>
                                ) : null}
                            </span>
                        ) : null}
                    </div>
                    <div className="h-1 rounded-full border border-solid border-[#e4e4e7] bg-white">
                        <div className="h-1 rounded-full bg-[#3f3f46]" style={{ width: `${sharePercentage}%` }} />
                    </div>
                </div>

                <div className="mt-3 gap-1 w-full">
                    <div className={`flex max-h-[250px] flex-col gap-1 overflow-y-auto pr-1 ${THIN_SCROLLBAR_CLASS}`}>
                        {items.map((item, index) => (
                            <Popover
                                key={`${item.description ?? 'item'}-${index}`}
                                trigger="hover"
                                placement="rightTop"
                                showArrow={false}
                                overlayInnerStyle={{ padding: 0, background: 'transparent', boxShadow: 'none' }}
                                content={<WhitespaceItemTooltip item={item} selectedYAxisKpiId={selectedYAxisKpiId} />}
                            >
                                <span
                                    className={`w-fit cursor-pointer rounded px-1 py-[1px] text-xs bg-white ${
                                        item.is_own_attribute ? 'shadow-[0px_3px_3px_rgba(0,0,0,0.12),0px_9px_14px_rgba(0,0,0,0.05)]' : 'opacity-70'
                                    }`}
                                >
                                    {truncateLabel(item.description)}
                                </span>
                            </Popover>
                        ))}
                    </div>
                    {showCollapseButton || moreCount > 0 ? (
                        <div className="mt-2 flex items-center gap-3 text-xs text-[#71717b]">
                            {showCollapseButton ? (
                                <button
                                    type="button"
                                    onClick={onCollapseClick}
                                    className="flex items-center gap-1 border-none bg-transparent p-0 text-xs text-[#71717b]"
                                >
                                    <span>Collapse</span>
                                    <span className="inline-flex rotate-180">
                                        <HeaderSortDescChevron color={'#71717B'} />
                                    </span>
                                </button>
                            ) : null}
                            {moreCount > 0 ? (
                                <button
                                    type="button"
                                    onClick={onMoreClick}
                                    disabled={isMoreLoading}
                                    className="flex items-center gap-1 border-none bg-transparent p-0 text-xs text-[#71717b] disabled:opacity-60"
                                >
                                    <span>{`+ ${moreCount} more`}</span>
                                    <HeaderSortDescChevron color={'#71717B'} />
                                </button>
                            ) : null}
                        </div>
                    ) : null}
                </div>
            </div>
        );
    },
);

export default StaticWhitespaceCard;
